import { useAuth } from "@/contexts/AuthContext";
import { canonicalTagSignature } from "@/lib/tags";
import { DietProfileNote } from "@/components/DietProfileNote";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { cookbookPath } from "@/lib/slug";
import api from "@/lib/api";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import AdminModerationButtons from "@/components/AdminModerationButtons";
import RecipeCard from "@/components/RecipeCard";
import AutoSeo from "@/components/AutoSeo";
import ShareButtons from "@/components/ShareButtons";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const SEED_TAGS = [
  "Meat", "Cakes", "Fish", "Vegan", "Vegetarian", "Pescatarian",
  "Drinks", "Breakfast", "Desserts", "Biscuits", "Cookies", "Bread",
  "BBQ", "Dinners", "Pies", "Soups", "Sauces", "Snacks", "Roasts",
  "Family", "Kids", "Baby",
  "Italian", "Chinese", "French", "Indian", "Thai", "Mexican", "Spanish",
  // Dietary requirements — matched to the tag strings the AI batch tools
  // stamp on generated recipes so users clicking a diet chip see exactly
  // the recipes drafted under that diet's safety rules.
  "Gluten-Free", "Dairy-Free", "Egg-Free", "Nut-Free", "Soy-Free",
  "Low-FODMAP", "Low-Histamine", "Diabetic-Friendly", "Renal-Friendly",
];

export default function Directory() {
  /* The three search entry points (free-text `q`, letter A–Z, and tag chip)
   * are UX-mutually-exclusive: picking one clears the other two. Reported
   * bug: users would type in the search box, then click a letter, then a
   * tag — filters silently AND-combined, results went empty, and the fix
   * required a page refresh. Now every helper below owns the "clear the
   * other two" behaviour, and the search input debounces live so nothing
   * hides behind an Enter-key requirement.
   *
   * The Sort dropdown is a modifier (orthogonal to which filter is active),
   * so switching sort does NOT clear other filters. */
  const [letter, setLetter] = useState(null);
  const { user } = useAuth();
  const [dietOff, setDietOff] = useState(false);
  const [availTags, setAvailTags] = useState(null);
  const [popularTags, setPopularTags] = useState([]);
  useEffect(() => {
    api.get("/directory/tags", { params: { limit: 60 } }).then(({ data }) => {
      const taken = new Set(SEED_TAGS.map(canonicalTagSignature));
      const picks = [];
      for (const row of data || []) {
        const sig = canonicalTagSignature(row.tag);
        if (!sig || taken.has(sig) || /^(unknown|other|misc)$/i.test(row.tag)) continue;
        taken.add(sig);
        picks.push(row.tag);
        if (picks.length === 8) break;
      }
      setPopularTags(picks);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/directory/chip-availability", { params: { tags: SEED_TAGS.join(",") } })
      .then(({ data }) => setAvailTags(new Set(data.available || [])))
      .catch(() => setAvailTags(null));
  }, []);
  const profileDiets = dietOff ? [] : (user?.diet_profile || []);
  const [q, setQ] = useState("");
  const [inputVal, setInputVal] = useState("");   // uncontrolled draft for the search box (debounced into `q`)
  const [recipes, setRecipes] = useState([]);
  const [cookbooks, setCookbooks] = useState([]);
  const [tab, setTab] = useState("recipes");
  const [sort, setSort] = useState("alpha"); // "alpha" | "newest"
  const [tag, setTag] = useState(null);
  const [cuisine, setCuisineState] = useState(() => new URLSearchParams(window.location.search).get("cuisine") || "");
  const [cuisines, setCuisines] = useState([]);
  useEffect(() => { api.get("/directory/cuisines").then(({ data }) => setCuisines(data || [])).catch(() => {}); }, []);
  const setCuisine = (c) => {
    setCuisineState(c);
    const u = new URL(window.location.href);
    if (c) u.searchParams.set("cuisine", c); else u.searchParams.delete("cuisine");
    window.history.replaceState(null, "", u.toString());
  };
  const chooseLetter = (l) => {
    setLetter(l);
    setTag(null);
    setQ("");
    setInputVal("");
  };
  const chooseTag = (t) => {
    setTag(t);
    setLetter(null);
    setQ("");
    setInputVal("");
  };
  const clearAll = () => {
    setLetter(null);
    setTag(null);
    setQ("");
    setInputVal("");
    setCuisine("");
  };
  const hasFilter = !!(letter || tag || q || cuisine);

  // Debounce free-text search: type freely, results follow ~350ms later.
  // When the user starts typing, letter and tag filters are cleared so the
  // three modes stay mutually exclusive.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (inputVal.trim() !== q) {
        if (inputVal.trim()) {
          setLetter(null);
          setTag(null);
        }
        setQ(inputVal.trim());
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [inputVal, q]);

  /* Recipes are paginated with infinite scroll via an IntersectionObserver
   * on `sentinelRef`. Any filter change resets to page 0. When a fetch
   * returns fewer rows than PAGE_SIZE the list is exhausted (`hasMore=false`)
   * and no more requests fire.
   *
   * Previously the endpoint's default limit=60 was the ONLY slice fetched,
   * so an A→Z sort silently capped at ~K. */
  const PAGE_SIZE = 60;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const filterParams = () => {
    const params = new URLSearchParams();
    if (profileDiets.length) params.set("diet", profileDiets.join(","));
    if (letter) params.set("letter", letter);
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    if (tag) params.set("tag", tag);
    if (cuisine) params.set("cuisine", cuisine);
    return params;
  };

  // Reset paginated state whenever filters change; the effect below then
  // pulls page 0 fresh.
  useEffect(() => {
    const params = filterParams();
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", "0");
    setLoadingMore(true);
    Promise.all([
      api.get(`/directory/recipes?${params.toString()}`),
      api.get(`/directory/cookbooks?${filterParams().toString()}`),
    ]).then(([r, c]) => {
      setRecipes(r.data);
      setCookbooks(c.data);
      setOffset(r.data.length);
      setHasMore(r.data.length >= PAGE_SIZE);
    }).finally(() => setLoadingMore(false));
  }, [letter, sort, tag, q, cuisine, profileDiets.join()]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = filterParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      const r = await api.get(`/directory/recipes?${params.toString()}`);
      setRecipes((prev) => [...prev, ...r.data]);
      setOffset((prev) => prev + r.data.length);
      if (r.data.length < PAGE_SIZE) setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // IntersectionObserver → auto-load next page when the sentinel scrolls
  // into view. `rootMargin: 200px` starts the fetch just before the sentinel
  // is visible so the UX feels seamless.
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || tab !== "recipes") return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "200px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasMore, loadingMore, offset, tab, letter, sort, tag, q, cuisine]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
      <AutoSeo
        pageKey="directory"
        fallbackTitle="Directory — browse public family cookbooks & recipes"
        fallbackDescription="A growing library of family recipes from cooks around the world — fully searchable, free to browse, and printable as a real hardback cookbook."
      />
      <p className="overline mb-4">Public Directory</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]">
        Browse the world&apos;s<br />family kitchens.
      </h1>

      <div className="mb-6 sm:mb-10">
        <ShareButtons
          url={typeof window !== "undefined" ? window.location.href : ""}
          title="Browse public recipes on Recipe Filer"
          summary="A growing library of family recipes from cooks around the world."
          dataTestidPrefix="directory-share"
        />
      </div>

      <div className="flex flex-wrap gap-3 mb-8 items-center">
        <div className="flex bg-white rounded-full p-1 border border-rf-ink/10">
          <button
            data-testid="dir-tab-recipes"
            onClick={() => setTab("recipes")}
            className={`px-5 py-2 rounded-full text-sm font-bold ${tab === "recipes" ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft"}`}>
            Recipes
          </button>
          <button
            data-testid="dir-tab-cookbooks"
            onClick={() => setTab("cookbooks")}
            className={`px-5 py-2 rounded-full text-sm font-bold ${tab === "cookbooks" ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft"}`}>
            Cookbooks
          </button>
        </div>

        <div className="flex-1 min-w-[260px] relative">
          <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-rf-ink-soft pointer-events-none" size={20} />
          <input
            data-testid="dir-search-input"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            placeholder="Search by title…"
            className="input-rf !pl-14 !pr-10"
          />
          {inputVal && (
            <button
              onClick={() => { setInputVal(""); setQ(""); }}
              aria-label="Clear search"
              data-testid="dir-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-rf-ink-soft hover:text-rf-ink p-1"
            >
              <X size={16} weight="bold" />
            </button>
          )}
        </div>

        <select
          data-testid="dir-sort-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="input-rf !py-2.5 !w-auto font-bold text-sm"
          aria-label="Sort recipes"
        >
          <option value="alpha">A → Z</option>
          <option value="newest">Newest first</option>
        </select>

        {tab === "recipes" && cuisines.length > 0 && (
          <select
            data-testid="dir-cuisine-select"
            value={cuisine}
            onChange={(e) => setCuisine(e.target.value)}
            className={`input-rf !py-2.5 !w-auto font-bold text-sm ${cuisine ? "!border-rf-orange !bg-rf-amber/15" : ""}`}
            aria-label="Filter by cuisine"
          >
            <option value="">All cuisines</option>
            {cuisine && !cuisines.some((c) => c.cuisine.toLowerCase() === cuisine.toLowerCase()) && <option value={cuisine}>{cuisine}</option>}
            {cuisines.map((c) => <option key={c.cuisine} value={c.cuisine}>{c.cuisine} ({c.count})</option>)}
          </select>
        )}
        {tab === "recipes" && cuisine && (
          <Link to={`/cuisine/${cuisine.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} data-testid="dir-cuisine-page-link"
                className="text-sm font-bold text-rf-orange hover:underline whitespace-nowrap">About {cuisine} cooking →</Link>
        )}

        {hasFilter && (
          <button
            data-testid="dir-clear-filters"
            onClick={clearAll}
            className="text-sm font-bold text-rf-orange hover:underline flex items-center gap-1 whitespace-nowrap"
          >
            <X size={14} weight="bold" /> Clear filters
          </button>
        )}
      </div>

      {/* Tag filter chips — one swipeable row on phones */}
      <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 mobile-scroll-row" data-testid="dir-tag-row">
        <button
          data-testid="dir-tag-all"
          onClick={() => chooseTag(null)}
          className={`px-3 py-1.5 rounded-full text-sm font-bold ${!tag ? "bg-rf-orange text-rf-ink" : "bg-white text-rf-ink-soft border border-rf-ink/10 hover:bg-rf-cream-alt"}`}
        >All tags</button>
        {[...SEED_TAGS.filter((t) => !availTags || availTags.has(t) || tag === t), ...popularTags].map((t) => (
          <button
            key={t}
            data-testid={`dir-tag-${t.toLowerCase()}`}
            onClick={() => chooseTag(tag === t ? null : t)}
            className={`px-3 py-1.5 rounded-full text-sm font-bold ${tag === t ? "bg-rf-orange text-rf-ink" : "bg-white text-rf-ink-soft border border-rf-ink/10 hover:bg-rf-cream-alt"}`}
          >{t}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-8 sm:mb-10 mobile-scroll-row" data-testid="dir-letter-row">
        <button
          data-testid="dir-letter-all"
          onClick={() => chooseLetter(null)}
          className={`px-3 py-1.5 rounded-full text-sm font-bold ${!letter ? "bg-rf-amber text-rf-ink" : "bg-white text-rf-ink-soft border border-rf-ink/10"}`}
        >All</button>
        {LETTERS.map((l) => (
          <button
            key={l}
            data-testid={`dir-letter-${l}`}
            onClick={() => chooseLetter(letter === l ? null : l)}
            className={`w-9 h-9 rounded-full text-sm font-bold ${letter === l ? "bg-rf-amber text-rf-ink" : "bg-white text-rf-ink-soft border border-rf-ink/10 hover:bg-rf-cream-alt"}`}
          >{l}</button>
        ))}
      </div>

      {tab === "recipes" ? (
        <>
          <DietProfileNote diets={profileDiets} onShowAll={() => setDietOff(true)} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recipes.length === 0 && !loadingMore && <p className="text-rf-ink-soft col-span-full text-center py-10">No recipes match your filter yet.</p>}
            {recipes.map((r) => (
              <RecipeCard
                key={r.recipe_id}
                recipe={r}
                linkQuery="?from=directory"
                showCopy
                onAdminDelete={(rid) => setRecipes((prev) => prev.filter((x) => x.recipe_id !== rid))}
              />
            ))}
          </div>
          {/* Infinite-scroll sentinel — IntersectionObserver auto-fetches the
              next 60 as this element scrolls into view. The button is a
              no-JS/older-browser fallback so users on any device can still
              request more manually. */}
          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-10">
              {loadingMore ? (
                <p className="text-rf-ink-soft text-sm" data-testid="dir-loading-more">Loading more recipes…</p>
              ) : (
                <button
                  onClick={loadMore}
                  data-testid="dir-load-more"
                  className="btn-outline-ink"
                >Load more</button>
              )}
            </div>
          )}
          {!hasMore && recipes.length > 0 && (
            <p data-testid="dir-end-of-list" className="text-center text-rf-ink-soft text-sm py-8">
              You&apos;ve reached the end · {recipes.length} recipe{recipes.length === 1 ? "" : "s"}
            </p>
          )}
        </>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cookbooks.length === 0 && <p className="text-rf-ink-soft col-span-full text-center py-10">No public cookbooks yet.</p>}
          {cookbooks.map((c) => (
            <div key={c.cookbook_id} className="relative group">
              <AdminModerationButtons kind="cookbook" id={c.cookbook_id} label={c.title} onDelete={(cid) => setCookbooks((prev) => prev.filter((x) => x.cookbook_id !== cid))} />
              <Link to={`${cookbookPath(c)}?from=directory`} className="card-recipe hover-lift block">
                {c.cover_image_url && <div className="-m-6 mb-4 aspect-[16/9] overflow-hidden"><img src={c.cover_image_url} alt={`Cover of ${c.title} cookbook`} className="w-full h-full object-cover" /></div>}
                <h3 className="font-display font-extrabold text-2xl text-rf-ink mb-2 line-clamp-2">{c.title}</h3>
                <p className="text-rf-ink-soft text-sm line-clamp-2">{c.description}</p>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
